// The pure rules behind the per-account virtuals verdict, shared by the places
// that read it: ProvidersVisibilityService (the nav entries),
// ProvidersEnabledGuard (the /virtuals route), VirtualsVerdictRefreshService
// (when to ask the server again) and the user-details reducer (what an ERROR
// may replace). No Angular here, so the guard and the
// services can all import it without a dependency cycle, and the specs can
// drive every rule directly.
//
// The verdict itself is the SERVER's (GET /user/getUserAccount `virtuals`,
// design D1/D2): global setting, provider state, currency allowlist, rollout
// rule, block, role and account status, all decided in one place. The client
// adds exactly one rule of its own — the verdict applies only to the account
// it was computed for — and decides when a stored verdict is too old to trust.

/**
 * How long a stored verdict is trusted before the guard (or a tab coming back
 * to the foreground) asks the server again. Five minutes: the same window the
 * provider catalog cache used, short enough for an ops change (a rollout
 * list edit, a block, the global switch) to reach an open tab, and long
 * enough that ordinary navigation costs no extra account call.
 */
export const VIRTUALS_VERDICT_TTL_MS = 5 * 60 * 1000;

/**
 * The reason the backend stamps on a verdict it could not COMPUTE (design
 * D4: any throw while deciding — a Redis stall past its 1.5 s timeout, a
 * database hiccup — yields `{ visible: false, reason: 'ERROR' }` and never
 * fails the account call). Every other hidden reason is a decision; this one
 * is the absence of one, so no rule here treats it as an answer.
 */
export const VIRTUALS_VERDICT_ERROR_REASON = 'ERROR';

/** The fields of a verdict these rules read, whatever object carries them. */
export interface VirtualsVerdictLike {
  fbId?: string;
  visible?: unknown;
  reason?: unknown;
  checkedAt?: unknown;
}

/**
 * Is this the backend's "could not decide" answer rather than a decision?
 * Only a hidden verdict can be one: `visible: true` is always a decision.
 */
export function isVerdictComputeError(
  verdict: VirtualsVerdictLike | null | undefined
): boolean {
  return (
    !!verdict &&
    verdict.visible !== true &&
    verdict.reason === VIRTUALS_VERDICT_ERROR_REASON
  );
}

/**
 * Was this verdict computed for `uid`? An empty uid belongs to nobody, so a
 * cleared store ('' fbId) and a signed-out tab can never match each other.
 */
export function verdictBelongsTo(
  verdict: VirtualsVerdictLike | null | undefined,
  uid: string | null | undefined
): boolean {
  return !!uid && !!verdict && verdict.fbId === uid;
}

/**
 * Is a verdict stamped `checkedAt` (server epoch ms) still inside the TTL?
 *
 * `checkedAt` comes from the SERVER's clock and `now` from the browser's, so
 * the age is taken as an absolute distance: a client clock running behind
 * would otherwise see a verdict "from the future" and trust it for as long as
 * the skew lasts. The cost of that choice falls on a badly skewed client
 * (more than the TTL either way), which then re-asks on every trigger — the
 * guard entry and the tab coming back into view — and never on correctness.
 * 0, a missing stamp or a non-number reads as stale.
 */
export function isVerdictFresh(
  checkedAt: unknown,
  now: number,
  ttlMs: number = VIRTUALS_VERDICT_TTL_MS
): boolean {
  if (typeof checkedAt !== 'number' || !Number.isFinite(checkedAt) || checkedAt <= 0) {
    return false;
  }
  return Math.abs(now - checkedAt) <= ttlMs;
}

/**
 * Can a STORED verdict answer for `uid` right now, without a refetch? Only
 * when it is that account's, still fresh, and an actual decision. A missing,
 * foreign or stale one means "ask the server" — never "hidden" and never
 * "visible" — and so does a compute ERROR, however fresh: it says the server
 * could not answer, and trusting it for the TTL would hide the entry and
 * bounce /virtuals deep links for five minutes on one Redis stall.
 *
 * (The reducer never lets an ERROR replace a decision this account already
 * has — it keeps that decision and marks it stale, see
 * user-details.reducer.ts — so a stored ERROR only ever means "this account
 * has no decision yet".)
 */
export function isStoredVerdictUsable(
  verdict: VirtualsVerdictLike | null | undefined,
  uid: string | null | undefined,
  now: number
): boolean {
  return (
    verdictBelongsTo(verdict, uid) &&
    !isVerdictComputeError(verdict) &&
    isVerdictFresh(verdict.checkedAt, now)
  );
}

/**
 * The client half of the decision on a verdict-bearing object: it is `uid`'s
 * and the server said an explicit boolean `true`.
 */
export function isVerdictVisibleFor(
  verdict: VirtualsVerdictLike | null | undefined,
  uid: string | null | undefined
): boolean {
  return verdictBelongsTo(verdict, uid) && verdict.visible === true;
}

/**
 * The same decision on a RAW GET /user/getUserAccount payload, before the
 * reducer has seen it — what the guard decides from after a refetch. The
 * payload's own fbId is the account the token belonged to when the request
 * left, so a response that raced an account switch answers false here.
 */
export function isAccountPayloadVisibleFor(
  account: any,
  uid: string | null | undefined
): boolean {
  if (!account || typeof account !== 'object') {
    return false;
  }
  const verdict = account.virtuals;
  return isVerdictVisibleFor(
    {
      fbId: account.fbId,
      visible: verdict && typeof verdict === 'object' ? verdict.visible : undefined,
    },
    uid
  );
}

/**
 * The guard's decision after a refetch: the FRESH payload's verdict, except
 * when that verdict is a compute ERROR. Then the decision the store holds
 * for this same account stands — the reducer has just kept it in place of
 * the ERROR (marked stale, so the next trigger asks again) — and a /virtuals
 * deep link is not bounced home on a transient backend failure. With no
 * decision stored for `uid`, the ERROR stays what it is on the wire: hidden.
 */
export function isRefetchedVisibleFor(
  account: any,
  stored: VirtualsVerdictLike | null | undefined,
  uid: string | null | undefined
): boolean {
  const verdict = account && typeof account === 'object' ? account.virtuals : null;
  if (
    verdict &&
    typeof verdict === 'object' &&
    isVerdictComputeError(verdict) &&
    verdictBelongsTo({ fbId: account.fbId }, uid) &&
    verdictBelongsTo(stored, uid) &&
    !isVerdictComputeError(stored)
  ) {
    return stored.visible === true;
  }
  return isAccountPayloadVisibleFor(account, uid);
}
